import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { Plus, Trash2, Cpu, CheckCircle, AlertTriangle, Play, Activity } from "lucide-react";
import Modal from "../components/Modal";
import useCurrentUser from "../hooks/useCurrentUser";

export default function Workflow() {
    const { clientId, role } = useCurrentUser();
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [users, setUsers] = useState([
        { id: "admin", name: "Order Admin", role: "admin" },
        { id: "logistics", name: "Logistics Coordinator", role: "logistics" },
        { id: "planner", name: "Supply Planner", role: "planner" }
    ]);

    const [form, setForm] = useState({
        name: "",
        triggerType: "inventory",
        conditionField: "Quantity",
        operator: "<",
        value: "",
        recipientRole: "admin"
    });

    const API_URL = "/api/workflows";

    useEffect(() => {
        fetchWorkflows();
    }, [clientId]);

    const fetchWorkflows = async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const res = await api.get(`${API_URL}?clientId=${clientId}`);
            setWorkflows(res.data);
        } catch (err) {
            console.error("Failed to fetch workflows", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post(API_URL, { ...form, clientId });
            setShowModal(false);
            setForm({
                name: "",
                triggerType: "inventory",
                conditionField: "Quantity",
                operator: "<",
                value: "",
                recipientRole: "admin"
            });
            fetchWorkflows();
        } catch (err) {
            console.error("Failed to create workflow", err);
            alert("Error creating workflow");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this workflow?")) return;
        try {
            await api.delete(`${API_URL}?clientId=${clientId}&id=${id}`);
            fetchWorkflows();
        } catch (err) {
            console.error("Failed to delete workflow", err);
        }
    };

    return (
        <div className="max-w-7xl mx-auto pb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">Automation Workflows</h1>
                    <p className="text-slate-500 mt-1">Configure automated triggers and notifications.</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="px-4 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 font-medium flex items-center gap-2 transition-colors shadow-lg shadow-navy-900/20"
                >
                    <Plus size={18} /> New Workflow
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Workflows</p>
                        <h3 className="text-2xl font-bold text-navy-900 mt-2">{workflows.length}</h3>
                    </div>
                    <div className="p-3 rounded-full bg-blue-50 text-blue-600">
                        <Cpu size={24} />
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Triggers (24h)</p>
                        <h3 className="text-2xl font-bold text-navy-900 mt-2">
                            {workflows.filter(w => w.lastTriggered && new Date(w.lastTriggered._seconds * 1000) > Date.now() - 86400000).length}
                        </h3>
                    </div>
                    <div className="p-3 rounded-full bg-emerald-50 text-emerald-600">
                        <Activity size={24} />
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-slate-500">Loading workflows...</div>
                ) : workflows.length === 0 ? (
                    <div className="p-12 text-center text-slate-400">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Cpu size={32} className="text-slate-300" />
                        </div>
                        <p>No workflows configured.</p>
                        <button onClick={() => setShowModal(true)} className="text-blue-600 font-medium mt-2 hover:underline">Create your first trigger</button>
                    </div>
                ) : (
                    <table className="min-w-full text-left">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase">Workflow Name</th>
                                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase">Trigger Condition</th>
                                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase">Recipient</th>
                                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase">Status</th>
                                <th className="px-6 py-4 text-xs font-bold text-navy-900 uppercase text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {workflows.map((w) => (
                                <tr key={w.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-navy-900">{w.name}</div>
                                        <div className="text-xs text-slate-500 capitalize">{w.triggerType} Trigger</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono text-slate-700 border border-slate-200">
                                            {w.conditionField} {w.operator} {w.value}
                                        </code>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm text-slate-600 flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                                            {users.find(u => u.role === w.recipientRole)?.name || w.recipientRole}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                            <Play size={10} fill="currentColor" /> Active
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => handleDelete(w.id)}
                                            className="text-slate-400 hover:text-red-600 transition-colors p-2 hover:bg-red-50 rounded"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal open={showModal} onClose={() => setShowModal(false)}>
                <div className="p-6">
                    <h2 className="text-xl font-bold text-navy-900 mb-6 font-serif">Create New Workflow</h2>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-navy-900 mb-1.5">Workflow Name</label>
                            <input
                                type="text"
                                required
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                placeholder="e.g. Low Stock Alert"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Trigger Type</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                    value={form.triggerType}
                                    onChange={e => setForm({ ...form, triggerType: e.target.value })}
                                >
                                    <option value="inventory">Inventory</option>
                                    <option value="customs">Customs</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Field</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                    value={form.conditionField}
                                    onChange={e => setForm({ ...form, conditionField: e.target.value })}
                                >
                                    {form.triggerType === "inventory" ? (
                                        <>
                                            <option value="Quantity">Quantity</option>
                                            <option value="Value">Value</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="status">Status</option>
                                            <option value="eta">ETA</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1">
                                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Operator</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                    value={form.operator}
                                    onChange={e => setForm({ ...form, operator: e.target.value })}
                                >
                                    <option value="<">Less than</option>
                                    <option value=">">Greater than</option>
                                    <option value="=">Equals</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Value</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                    placeholder={form.triggerType === 'inventory' ? "e.g. 100" : "e.g. Cleared"}
                                    value={form.value}
                                    onChange={e => setForm({ ...form, value: e.target.value })}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-navy-900 mb-1.5">Recipient Role</label>
                            <select
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                                value={form.recipientRole}
                                onChange={e => setForm({ ...form, recipientRole: e.target.value })}
                            >
                                {users.map(u => (
                                    <option key={u.id} value={u.role}>{u.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                            <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Cancel</button>
                            <button type="submit" className="px-6 py-2 bg-navy-900 text-white rounded-lg shadow hover:bg-navy-800">Create Workflow</button>
                        </div>

                    </form>
                </div>
            </Modal>
        </div>
    );
}
